import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import configService, { 
  TaxRate, 
  CreateTaxRateDto, 
  UpdateTaxRateDto, 
  TaxRateFilters,
  PaginatedResponse 
} from '@/services/configService'

interface TaxRateState {
  taxRates: TaxRate[]
  currentTaxRate: TaxRate | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: TaxRateFilters

  // Actions
  setFilters: (filters: TaxRateFilters) => void
  fetchTaxRates: (filters?: TaxRateFilters) => Promise<void>
  fetchTaxRateById: (id: string) => Promise<void>
  createTaxRate: (data: CreateTaxRateDto) => Promise<TaxRate>
  updateTaxRate: (id: string, data: UpdateTaxRateDto) => Promise<TaxRate>
  deleteTaxRate: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentTaxRate: () => void
}

const useTaxRateStore = create<TaxRateState>()(
  devtools(
    (set, get) => ({
      taxRates: [],
      currentTaxRate: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchTaxRates: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<TaxRate> = await configService.taxRates.getAll(currentFilters)
          
          set({
            taxRates: response.data,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: response.total,
              pages: Math.ceil(response.total / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des taux de taxe',
            loading: false
          })
        }
      },

      fetchTaxRateById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const taxRate = await configService.taxRates.getById(id)
          set({ currentTaxRate: taxRate, loading: false })
        } catch (error: any) {
          console.error('Error fetching tax rate by ID:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors du chargement du taux de taxe'
          set({
            error: errorMessage,
            loading: false
          })
        }
      },

      createTaxRate: async (data: CreateTaxRateDto) => {
        try {
          set({ loading: true, error: null })
          const taxRate = await configService.taxRates.create(data)
          
          // Add to taxRates list
          set((state) => ({
            taxRates: [taxRate, ...state.taxRates],
            loading: false
          }))

          return taxRate
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création du taux de taxe',
            loading: false
          })
          throw error
        }
      },

      updateTaxRate: async (id: string, data: UpdateTaxRateDto) => {
        try {
          set({ loading: true, error: null })
          const taxRate = await configService.taxRates.update(id, data)
          
          // Update in taxRates list
          set((state) => ({
            taxRates: state.taxRates.map(t => 
              t.id === id ? taxRate : t
            ),
            currentTaxRate: state.currentTaxRate?.id === id ? taxRate : state.currentTaxRate,
            loading: false
          }))

          return taxRate
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour du taux de taxe',
            loading: false
          })
          throw error
        }
      },

      deleteTaxRate: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await configService.taxRates.delete(id)
          
          // Remove from taxRates list
          set((state) => ({
            taxRates: state.taxRates.filter(t => t.id !== id),
            currentTaxRate: state.currentTaxRate?.id === id ? null : state.currentTaxRate,
            loading: false
          }))
        } catch (error: any) {
          console.error('Error deleting tax rate:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors de la suppression du taux de taxe'
          set({
            error: errorMessage,
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentTaxRate: () => set({ currentTaxRate: null })
    }),
    { name: 'TaxRateStore' }
  )
)

export default useTaxRateStore
